import { List, Map } from 'immutable';
import { camelCase } from 'lodash';
import { INSPECTIONS, VIOLATIONS } from './../app.constants';
import createSelector from '../../utils/reselect';
import { History, Comment, LicenseSubview } from '../modules/application/types';

/*
 * getApplication will give the parent state of the application reducer
 * and we will call it with all other functions so that we can access inner objects
 */
export const getApplication = (state: any) => {
    const { application } = state;
    return application || Map();
};
/*
 * getApplicationList will return application list
 */
export const getApplicationList = createSelector(getApplication, (state) => {
    return state.getIn(['applicationList', 'data']);
});
/*
 * getApplicationListLoading will return application list loading state
 */
export const getApplicationListLoading = createSelector(getApplication, (state) => {
    return state.getIn(['applicationList', 'loading']) || false;
});
/*
 * getFilteredApplicationListLoading will return application list loading state while filtering
 */
export const getFilteredApplicationListLoading = createSelector(getApplication, (state) => {
    return state.getIn(['applicationList', 'filterLoading']) || false;
});
/*
 * load more boolean to see if there are more record on server
 */
export const getLoadMore = createSelector(getApplication, (state) => {
    return state.getIn(['applicationList', 'loadMore']);
});

/*
 * getApplicationDetail will return applications detail object
 */
export const getApplicationDetail = createSelector(getApplication, (state) => {
    return state.getIn(['applicationDetail', 'data']) || Map();
});
/*
 * getLicenseType will return the license type configuration
 */
export const getLicenseType = createSelector(getApplication, (state) => {
    return state.getIn(['licenseType', 'data']) || Map();
});
/*
 * getApplicationEditForm will return edit form object
 */
export const getApplicationEditForm = createSelector(getApplication, (state) => {
    return state.getIn(['applicationEditForm', 'data']) || Map();
});

/**
 * getLicenseAccounting will return accounting records for the license
 */
export const getLicenseAccounting = createSelector(getApplication, (state) => {
    return state.getIn(['licenseAccounting', 'data']) || List();
});

export const getLicenseAccountingStatus = createSelector(getApplication, (state) => {
    return state.getIn(['licenseAccounting', 'loading']);
});

/*
 * Return error status
 */
export const getLicenseAccountingErrorStatus = createSelector(getApplication, (state) => {
    return state.getIn(['licenseAccounting', 'error']);
});

/*
 * getAuditHistoryList will return  audit history list status
 */
export const getAuditHistoryList = createSelector(getApplication, (state) => {
    const auditHistoryList = state.getIn(['applicationHistories', 'data']) || List();

    return auditHistoryList.sort(function (a: History, b: History) {
        const date1 = new Date(b.date).valueOf();
        const date2 = new Date(a.date).valueOf();
        return date1 - date2;
    });
});
export const getAuditHistoryListStatus = createSelector(getApplication, (state) => {
    return state.getIn(['applicationHistories', 'loading']);
});

export const getAuditHistoryHasMore = createSelector(getApplication, (state) => {
    return state.getIn(['applicationHistories', 'hasMore']);
});
/*
 * getLicenseListSearchedLoader will return searched Licenses Loader State
 */
export const getLicenseListSearchedLoader = createSelector(getApplication, (state) => {
    return state.getIn(['applicationListSearched', 'loading']);
});
/*
 * getLicenseListSearched will return searched Licenses
 */
export const getLicenseListSearched = createSelector(getApplication, (state) => {
    return state.getIn(['applicationListSearched', 'data']) || List();
});
/*
 * getUpdateLicenseLoader will return update license api loader status
 */
export const getUpdateLicenseLoader = createSelector(getApplication, (state) => {
    return state.getIn(['applicationData', 'loading']);
});
/*
 * getUpdateLicenseLoader will return update license api loader status
 */
export const getListFilters = createSelector(getApplication, (state) => {
    return (
        state.getIn(['licenseFilters', 'listFilters']) || {
            licenseType: [],
            licenseStatus: [],
            fromDate: '',
            toDate: '',
        }
    );
});

/*
 * getUpdateLicenseLoader will return update license api loader status
 */
export const getLicenseTypes = createSelector(getApplication, (state) => {
    return state.getIn(['licenseTypes', 'data']) || List();
});

/*
 * getLicensesCount will return total license count and filtered licenses count
 */
export const getLicensesCount = createSelector(getApplication, (state) => {
    return state.getIn(['applicationList', 'licensesCount']);
});

/*
 * getViolationList will return application violation list
 */
export const getViolationList = createSelector(getApplication, (state) => {
    return state.getIn(['applicationViolation', 'data']) || List();
});
/*
 * getViolationsListLoadingStatus will return get files api loader status
 */
export const getViolationsListLoadingStatus = createSelector(getApplication, (state) => {
    return state.getIn(['applicationViolation', 'loading']);
});

/*
 * getInspectionList will return application Inspection list
 */
export const getInspectionList = createSelector(getApplication, (state) => {
    return state.getIn(['applicationInspection', 'data']) || List();
});
/*
 * getInspectionListLoadingStatus will return get inspection api loader status
 */
export const getInspectionListLoadingStatus = createSelector(getApplication, (state) => {
    return state.getIn(['applicationInspection', 'loading']);
});
/*
 * getCategoryList will return get Categories api data
 */
export const getCategoryList = createSelector(getApplication, (state) => {
    return state.getIn(['applicationCategoryList', 'data']) || List();
});
/*
 * getCategoryListLoadingStatus will return get Category api loader status
 */
export const getCategoryListLoadingStatus = createSelector(getApplication, (state) => {
    return state.getIn(['applicationCategoryList', 'loading']);
});
/*
 * getUserList will return get User api data
 */
export const getUserList = createSelector(getApplication, (state) => {
    return state.getIn(['addInspectionUserList', 'data']) || List();
});
/*
 * getUserListLoadingStatus will return get User api loader status
 */
export const getUserListLoadingStatus = createSelector(getApplication, (state) => {
    return state.getIn(['addInspectionUserList', 'loading']);
});

/*
 * getInspectionType will return get inspection type api data
 */
export const getInspectionType = createSelector(getApplication, (state) => {
    return state.getIn(['inspectionTypeList', 'data']) || List();
});
/*
 * getInspectionTypeLoadingStatus will return inspection type api loader status
 */
export const getInspectionTypeLoadingStatus = createSelector(getApplication, (state) => {
    return state.getIn(['inspectionTypeList', 'loading']);
});

export const getCommentList = createSelector(getApplication, (state) => {
    const commentList = state.getIn(['applicationComment', 'data']) || List();
    return commentList.sort(function (a: Comment, b: Comment) {
        const date1 = new Date(b.timestamp).valueOf();
        const date2 = new Date(a.timestamp).valueOf();
        return date1 - date2;
    });
});
/*
 * getCommentListLoadingStatus will return comment list api loader status
 */
export const getCommentListLoadingStatus = createSelector(getApplication, (state) => {
    return state.getIn(['applicationComment', 'loading']);
});

export const getActiveTab = createSelector(getApplication, (state) => {
    return state.getIn(['activeTab']);
});
/*
 * getInspectionEditForm will return edit form object
 */
export const getInspectionEditForm = createSelector(getApplication, (state) => {
    return state.getIn(['inspectionEditForm', 'data']) || Map();
});
/*
 * getInspectionItem will return a single inspection object
 */
export const getInspectionItem = createSelector(getApplication, (state) => {
    const editFormData = state.getIn(['inspectionEditForm', 'data']);
    const inspectionDetailList = state.getIn(['applicationInspection', 'data']);
    const inspectionDetail = inspectionDetailList?.filter(
        (inspectionItem: any) => inspectionItem.id === editFormData?.id,
    )[0];
    return inspectionDetail || Map();
});

export const getApplicationPersonDetails = createSelector(
    getApplication,
    getApplicationDetail,
    (state, applicationDetail) => applicationDetail.person ?? null,
);

export const getMergeTemplatesLoadingStatus = createSelector(getApplication, (state) => {
    return state.getIn(['templates', 'loading']);
});

export const getMergeTemplatesList = createSelector(getApplication, (state) => {
    return state.getIn(['templates', 'data']) || List();
});

/*
 * getPersonListSearchedLoader will return searched Persons Loader State
 */
export const getPersonListSearchedLoader = createSelector(getApplication, (state) => {
    return state.getIn(['personListSearched', 'loading']);
});
/*
 * getPersonListSearched will return searched Persons
 */
export const getPersonListSearched = createSelector(getApplication, (state) => {
    return state.getIn(['personListSearched', 'data']) || List();
});

/*
 * load more boolean to see if there are more record for inspection on server
 */
export const getLoadMoreInspection = createSelector(getApplication, (state) => {
    return state.getIn(['applicationInspection', 'loadMore']);
});
/* getPrintDocumentsHistoryList will return searched Persons
 */
export const getPrintDocumentsHistoryList = createSelector(getApplication, (state) => {
    return state.getIn(['printDocumentsHistoryList', 'data']) || List();
});

/*
 * getPersonListSearchedLoader will return searched Persons Loader State
 */
export const getPrintDocumentsHistoryLoading = createSelector(getApplication, (state) => {
    return state.getIn(['printDocumentsHistoryList', 'loading']);
});

/* load more boolean to see if there are more record on server
 */
export const getLoadMoreDocuments = createSelector(getApplication, (state) => {
    return state.getIn(['printDocumentsHistoryList', 'loadMore']);
});
/*
 * load more boolean to see if there are more record for violations on server
 */
export const getLoadMoreViolation = createSelector(getApplication, (state) => {
    return state.getIn(['applicationViolation', 'loadMore']);
});
/*
 * load more boolean to see if there are more record for commentList on server
 */
export const getLoadMoreCommentList = createSelector(getApplication, (state) => {
    return state.getIn(['applicationComment', 'loadMore']);
});

export const getLoadMoreFile = createSelector(getApplication, (state) => {
    return state.getIn(['applicationFiles', 'loadMore']);
});

export const getLoadMoreAccountingList = createSelector(getApplication, (state) => {
    return state.getIn(['licenseAccounting', 'loadMore']);
});

/*
 * getInspectionCount will return total inspections count
 */
export const getInspectionCount = createSelector(getApplication, (state) => {
    return state.getIn(['applicationInspection', 'inspectionCount']);
});

export const getInspectionColumnFields = createSelector(getApplication, (state) => {
    const subViews = state.getIn(['licenseType', 'data', 'views', 'edit', 'subviews']);
    return subViews && subViews.length ? subViews.find((v: LicenseSubview) => v.id === camelCase(INSPECTIONS)) : [];
});

export const getLicenseTabs = createSelector(getApplication, (state) => {
    const subViews = state.getIn(['licenseType', 'data', 'views', 'edit', 'subviews']);
    return subViews;
});

export const getViolationColumnFields = createSelector(getApplication, (state) => {
    const subViews = state.getIn(['licenseType', 'data', 'views', 'edit', 'subviews']);
    return subViews && subViews.length ? subViews.find((v: LicenseSubview) => v.id === camelCase(VIOLATIONS)) : [];
});

export const getLicensesSort = createSelector(getApplication, (state) => {
    return state.get('licenseSort')?.toJS();
});
